import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Divider,
  Dropdown,
  DropdownItem,
  LinkButton,
  Text,
  ThemeProvider,
} from '@amzn/storm-ui';
import { selectSignOutInfo, UserInfo } from '../../../header/store/slice';
import {
  MobileLanguageSwitchBtn,
  MobileLanguageSwitcher,
  SecondaryMenuWrapper,
} from './styles';
import handleLanguageSwitch from 'src/features/header/components/LanguageSwitcher/handle-language-switch';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import ThemeModesControl from 'src/features/header/components/ThemeModesControl/ThemeModesControl';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { HeaderBundleProvider } from 'src/providers/PantherLocalizationProvider';
import { StyledLink } from 'src/commons/components/StyledLink';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';

const UserPreferenceView: React.VFC<UserInfo> = (userInfo) => {
  const initialLanguage = userInfo.languageInfo.currentLocale;
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const signOutInfo = useSelector(selectSignOutInfo);

  const isChromeThemeModesControlEnabled = useIsFeatureEnabled(
    WeblabedFeatures.CHROME_THEME_MODES_CONTROL,
  );
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const isThemeModesControlVisible =
    isChromeThemeModesControlEnabled &&
    !!internalChromeConfig.isDarkModeEnabled;

  const LanguageSwitcherOptions = userInfo.languageInfo.languageOptions.map(
    (language) => (
      <DropdownItem value={language.locale} key={language.locale}>
        {language.label}
      </DropdownItem>
    ),
  );

  return (
    <>
      <SecondaryMenuWrapper>
        <Text color="secondary" size="large">
          {userInfo.signedInAsLabel}
        </Text>
        <Text size="large">
          <strong>{userInfo.customerInfo?.fullName || ''}</strong>
        </Text>
        <Text color="secondary" size="large">
          {userInfo.customerInfo?.primaryEmail || ''}
        </Text>
      </SecondaryMenuWrapper>
      <Divider marginBlockStart="xsmall" marginBlockEnd="xsmall" />
      <SecondaryMenuWrapper gap="xsmall">
        {userInfo.switchAccountInfo?.url && (
          <LinkButton
            href={userInfo.switchAccountInfo?.url}
            {...getTaktAttributes('switch-account-link', chromeComponents.NAV, {
              url: userInfo.switchAccountInfo?.url,
            })}
          >
            {userInfo.switchAccountInfo?.label}
          </LinkButton>
        )}
        <LinkButton
          href={userInfo.signOutInfo.url}
          {...getTaktAttributes('sign-out', chromeComponents.NAV, {
            url: userInfo.signOutInfo?.url,
          })}
        >
          {userInfo.signOutInfo.label}
        </LinkButton>
      </SecondaryMenuWrapper>
      <Divider marginBlockStart="xsmall" marginBlockEnd="xsmall" />
      {userInfo.notificationSettingsInfo && (
        <>
          <SecondaryMenuWrapper>
            <StyledLink
              href={userInfo.notificationSettingsInfo.url}
              {...getTaktAttributes(
                'notification-settings',
                chromeComponents.NAV,
                { url: userInfo.notificationSettingsInfo?.url },
              )}
            >
              {userInfo.notificationSettingsInfo.label}
            </StyledLink>
          </SecondaryMenuWrapper>
          <Divider marginBlockStart="xsmall" marginBlockEnd="xsmall" />
        </>
      )}
      <SecondaryMenuWrapper>
        <MobileLanguageSwitcher>
          <Dropdown
            label={
              userInfo.languageInfo.label.charAt(0) +
              userInfo.languageInfo.label.substring(1).toLowerCase()
            }
            onChange={(language) => setSelectedLanguage(language)}
            selectedValue={selectedLanguage}
            fullWidth={true}
            mobileFullScreen={true}
            {...getTaktAttributes(
              'aac-select-language',
              chromeComponents.NAV,
              userInfo.switchAccountInfo?.url
                ? {
                    url: userInfo.switchAccountInfo?.url,
                  }
                : {},
            )}
          >
            {LanguageSwitcherOptions}
          </Dropdown>
          {initialLanguage != selectedLanguage && (
            <MobileLanguageSwitchBtn
              variant="primary"
              onClick={async () =>
                await handleLanguageSwitch(
                  initialLanguage,
                  selectedLanguage,
                  signOutInfo.url,
                )
              }
              {...getTaktAttributes(
                'aac-language-switch-btn',
                chromeComponents.NAV,
              )}
            >
              {userInfo.languageInfo.changeLanguageText}
            </MobileLanguageSwitchBtn>
          )}
        </MobileLanguageSwitcher>
      </SecondaryMenuWrapper>
      {isThemeModesControlVisible && (
        <HeaderBundleProvider>
          <Divider marginBlockStart="xsmall" marginBlockEnd="xsmall" />
          <SecondaryMenuWrapper>
            <ThemeProvider enableThemeModes>
              <ThemeModesControl />
            </ThemeProvider>
          </SecondaryMenuWrapper>
        </HeaderBundleProvider>
      )}
    </>
  );
};

export default UserPreferenceView;
